import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { MotionConfig } from 'motion/react';
import { Capacitor } from '@capacitor/core';
import { router } from '@/routes/router';
import { useThemeEffect } from '@/hooks/useThemeEffect';
import { useTextSizeEffect } from '@/hooks/useTextSizeEffect';
import { Toaster } from '@/components/ui';
import { AppErrorBoundary } from '@/components/shell/AppErrorBoundary';
// Self-hosted brand type (woff2 bundled by Vite) — works offline in the native
// WebView and keeps web first paint off the Google Fonts CDN. Only the weights
// the app uses: Geist 400/500/600, Geist Mono 400/500, and the v4 display face
// Literata 400/500 (+ italics for the Narrator voice).
import '@fontsource/geist/400.css';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/literata/400.css';
import '@fontsource/literata/500.css';
import '@fontsource/literata/400-italic.css';
import '@fontsource/literata/500-italic.css';
import '@/styles/globals.css';

import { appCheckReady } from '@/lib/firebase';
import { initCrashReporter, setCrashUser } from '@/lib/crashReporter';
import { initAnalytics } from '@/lib/initAnalytics';
import { useAuthStore } from '@/stores/authStore';

import { initNotifications } from '@/features/notifications/initNotifications';
import { initNotificationListeners } from '@/features/notifications/notificationActions';
void initNotifications();
void initNotificationListeners();

// Crash/error reporting (native only; web is a no-op). Attribute reports to the
// signed-in Firebase uid, updating on auth changes.
initCrashReporter();
setCrashUser(useAuthStore.getState().uid);
useAuthStore.subscribe((s, prev) => {
  if (s.uid !== prev.uid) setCrashUser(s.uid);
});

// Web analytics (GA4). No-ops unless VITE_GA4_MEASUREMENT_ID is set.
initAnalytics();

// Pinch-zoom stays disabled inside the native WKWebView (index.html ships the
// app-like no-zoom viewport), but the same bundle serves the web app, where
// blocking zoom fails WCAG 1.4.4 Resize Text — so relax the viewport on web.
if (!Capacitor.isNativePlatform()) {
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
}

function ThemeBoot({ children }: { children: React.ReactNode }) {
  useThemeEffect();
  useTextSizeEffect();
  return (
    <>
      {children}
      <Toaster />
    </>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element found');

// Render immediately — don't block first paint on App Check / the Firebase SDK.
// App Check is already initializing (initAppCheck runs at firebase.ts import),
// and Firebase callables await the App Check token internally before firing, so
// nothing in the first paint depends on appCheckReady. Critical for cold-start
// on the marketing "/" URL in the iOS-15 WebView.
createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <ThemeBoot>
        {/* Belt-and-braces: Motion (WAAPI/JS) animations don't see the CSS
            prefers-reduced-motion squash in globals.css — this makes every
            motion.* component honor the OS setting even where a component
            forgets its own useReducedMotion guard. */}
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>
      </ThemeBoot>
    </AppErrorBoundary>
  </StrictMode>,
);

// Keep App Check initialization retained (and surface any init error to console).
void appCheckReady;
